import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Hide,
  IconButton,
  useBreakpointValue,
  useColorModeValue,
  VStack,
  Text,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { Link, To, useMatch } from 'react-router-dom';

import { HEADER_HEIGHT } from './constants';
import { logout } from '~/contexts/UserAuthProvider';
import { PermissionGuard, PermissionsFn } from '~/core/PermissionGuard';
import { colors } from '~/packages/common-frontend/colors';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  MenuTree,
  MenuTreeItem,
} from '~/packages/common-frontend/components/MenuTree';
import { useExpandedStore } from '~/packages/common-frontend/hooks/useExpandedStore';
import {
  LayoutContextValue,
  useLayoutContext,
} from '~/packages/common-frontend/layout/LayoutProvider';
import { ColorScheme, Sizes } from '~/packages/common-frontend/theme';

// Hooks
const useAutoCollapseOnBreakpoint = () => {
  const layout = useLayoutContext();
  const navExpandedStore = useExpandedStore({ key: 'GlobalNav' });
  const [prev, setPrev] = useState<LayoutContextValue['breakpoint']>();
  useEffect(() => {
    if (prev !== layout.breakpoint) {
      // Transitioning -> "wide"
      if (layout.breakpoint === 'wide') {
        layout.setGlobalNavOpen(
          () => navExpandedStore.preference !== 'collapsed',
        );
      }
      // Transitioning -> "narrow" (from "mobile" or "wide")
      else if (layout.breakpoint === 'narrow') {
        layout.setGlobalNavOpen(
          () => navExpandedStore.preference === 'expanded',
        );
      }
      // Transitioning -> "mobile" (from "narrow")
      else if (layout.breakpoint === 'mobile') {
        layout.setGlobalNavOpen(() => false);
      }
      setPrev(layout.breakpoint);
    }
  }, [prev, layout, navExpandedStore.preference]);
};

// Helpers
const ambiguousPathToString = (to: To) => {
  if (typeof to === 'string') return to;
  if (typeof to.pathname === 'string') return to.pathname;
  throw Error(
    `GlobalNav item received an unexpected argument [to=${JSON.stringify(to)}]`,
  );
};

// Exports
type GlobalNavRouterLinkItemProps = {
  children?: React.ReactNode;
  icon?: React.ComponentProps<typeof IconButton>['icon'];
  to: To;
  end?: boolean;
  onClick?: React.ComponentProps<typeof IconButton>['onClick'];
  size?: Sizes;
  colorScheme?: ColorScheme;
  defaultIsOpen?: boolean;
  /** Nav items with children can store their expanded state in local storage using this key  */
  expandedStoreKey?: string;
  /** Whether clicking on the entire item expands children vs. a small chevron button  */
  fullSurfaceExpandsContent?: boolean;
  /** Don't shrink nav items to icon-only buttons when the layout.globalNavOpen === `true` */
  forceFullSize?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isVisibleFilter?: () => boolean;
} & ({ label: string } | { label?: React.ReactNode; 'aria-label': string });
export const GlobalNavRouterLinkItem = React.forwardRef<
  HTMLButtonElement,
  GlobalNavRouterLinkItemProps
>((props, ref) => {
  const {
    children,
    icon,
    to,
    end = false,
    onClick = () => undefined,
    size,
    colorScheme = 'v2-aurora',
    defaultIsOpen = true,
    expandedStoreKey,
    fullSurfaceExpandsContent = true,
    forceFullSize = false,
    isActive = false,
    isDisabled = false,
    isLoading = false,
    isVisibleFilter = () => true,
    ...rest
  } = props;

  const expandOffesetPadding = useBreakpointValue({
    base: '0rem',
    md: '1.75rem',
  });
  const layout = useLayoutContext();
  const match = useMatch({ path: ambiguousPathToString(to), end });
  if (!isVisibleFilter()) return null;

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (layout.breakpoint === 'mobile') {
      layout.setGlobalNavOpen(() => false);
    }
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Clicked to navigate to ${ambiguousPathToString(to)}`,
      level: Sentry.Severity.Info,
    });
    onClick(event);
  };
  if (!forceFullSize && !layout.globalNavOpen) {
    if (!icon) return null;
    return (
      <IconButtonV2
        ref={ref}
        icon={icon}
        aria-label={'aria-label' in rest ? rest['aria-label'] : rest.label}
        size={size}
        placement="right"
        variant="v2-ghost"
        colorScheme={colorScheme}
        isActive={!!match || isActive}
        isDisabled={isDisabled}
        isLoading={isLoading}
        onClick={handleOnClick}
        IconButtonProps={{
          as: Link,
          to,
        }}
      />
    );
  }

  return (
    <MenuTreeItem
      ref={ref}
      icon={icon}
      {...rest}
      size={size}
      expandOffsetPadding={expandOffesetPadding}
      defaultIsOpen={defaultIsOpen}
      expandedStoreKey={expandedStoreKey}
      colorScheme={colorScheme}
      fullSurfaceExpandsContent={fullSurfaceExpandsContent}
      isActive={!!match || isActive}
      isDisabled={isDisabled}
      isLoading={isLoading}
      ButtonProps={{
        as: Link,
        to,
        onClick: handleOnClick,
        fontWeight: size === 'sm' ? 400 : 500,
      }}
    >
      {children}
    </MenuTreeItem>
  );
});
GlobalNavRouterLinkItem.displayName = 'GlobalNavRouterLinkItem';

type GlobalNavRouterLinkItemWithUpsellProps = {
  permissions?: PermissionsFn;
  GlobalNavRouterLinkItemProps: React.ComponentProps<
    typeof GlobalNavRouterLinkItem
  >;
};
export const GlobalNavRouterLinkItemWithUpsell = React.forwardRef<
  HTMLButtonElement,
  GlobalNavRouterLinkItemWithUpsellProps
>((props, ref) => {
  const { permissions = () => 'allow', GlobalNavRouterLinkItemProps } = props;
  return (
    <PermissionGuard
      permissions={permissions}
      upsell={
        <GlobalNavRouterLinkItem
          ref={ref}
          {...GlobalNavRouterLinkItemProps}
          aria-label={
            'aria-label' in GlobalNavRouterLinkItemProps
              ? GlobalNavRouterLinkItemProps['aria-label']
              : GlobalNavRouterLinkItemProps.label
          }
          label={
            <Flex
              as="span"
              gap="1rem"
              display="inline-flex"
              width="100%"
              alignItems="baseline"
            >
              <Flex
                as="span"
                display="inline"
                textOverflow="ellipsis"
                overflow="hidden"
                whiteSpace="nowrap"
                width="100%"
              >
                {GlobalNavRouterLinkItemProps.label}
              </Flex>
              <Text
                as="span"
                background={colors.poppy[300]}
                color={colors.gray[950]}
                display="inline-flex"
                flexShrink={0}
                // Using `em`s here is important. `<GlobalNavRouterLinkItem />`s come in different
                // sizes, so we can't hard-code `rem` values here. Using `em` lets us adjust this
                // badge's font-size based on the font-size value of the parent.
                fontSize="0.875em"
                padding="0.25em 0.5em"
                borderRadius="0.5em"
              >
                Pro
              </Text>
            </Flex>
          }
          to="https://www.finleycms.com/starter-plan"
        />
      }
    >
      <GlobalNavRouterLinkItem ref={ref} {...GlobalNavRouterLinkItemProps} />
    </PermissionGuard>
  );
});
GlobalNavRouterLinkItemWithUpsell.displayName =
  'GlobalNavRouterLinkItemWithUpsell';

type GlobalNavButtonItemProps = {
  children?: React.ReactNode;
  icon: React.ComponentProps<typeof IconButton>['icon'];
  label: string;
  onClick?: React.ComponentProps<typeof IconButton>['onClick'];
  size?: Sizes;
  colorScheme?: ColorScheme;
  defaultIsOpen?: boolean;
  /** Nav items with children can store their expanded state in local storage using this key  */
  expandedStoreKey?: string;
  /** Whether clicking on the entire item expands children vs. a small chevron button  */
  fullSurfaceExpandsContent?: boolean;
  /** Don't shrink nav items to icon-only buttons when the layout.globalNavOpen === `true` */
  forceFullSize?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isVisibleFilter?: () => boolean;
};
export const GlobalNavButtonItem = React.forwardRef<
  HTMLButtonElement,
  GlobalNavButtonItemProps
>((props, ref) => {
  const {
    children,
    icon,
    label,
    onClick = () => undefined,
    size,
    colorScheme = 'v2-aurora',
    defaultIsOpen = true,
    expandedStoreKey,
    fullSurfaceExpandsContent = true,
    forceFullSize = false,
    isActive = false,
    isDisabled = false,
    isLoading = false,
    isVisibleFilter = () => true,
  } = props;
  const layout = useLayoutContext();
  if (!isVisibleFilter()) return null;

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (layout.breakpoint === 'mobile') {
      layout.setGlobalNavOpen(() => false);
    }
    onClick(event);
  };
  if (!forceFullSize && !layout.globalNavOpen) {
    if (!icon) return null;
    return (
      <IconButtonV2
        ref={ref}
        icon={icon}
        aria-label={label}
        size={size}
        placement="right"
        variant="v2-ghost"
        colorScheme={colorScheme}
        isActive={isActive}
        isDisabled={isDisabled}
        isLoading={isLoading}
        onClick={handleOnClick}
      />
    );
  }

  return (
    <MenuTreeItem
      ref={ref}
      icon={icon}
      label={label}
      size={size}
      colorScheme={colorScheme}
      expandOffsetPadding="1.75rem"
      expandedStoreKey={expandedStoreKey}
      defaultIsOpen={defaultIsOpen}
      fullSurfaceExpandsContent={fullSurfaceExpandsContent}
      isActive={isActive}
      isDisabled={isDisabled}
      isLoading={isLoading}
      ButtonProps={{
        onClick,
        fontWeight: size === 'sm' ? 400 : 500,
      }}
    >
      {children}
    </MenuTreeItem>
  );
});
GlobalNavButtonItem.displayName = 'GlobalNavButtonItem';

const LogoutButton = () => (
  <Flex direction="column" padding="0 0.75rem" paddingBottom="0.75rem">
    <GlobalNavButtonItem
      icon={<Icon icon="arrow-right-from-bracket" />}
      label="Log Out"
      onClick={() => logout()}
    />
  </Flex>
);

const CollapseButton = () => {
  const layout = useLayoutContext();
  const navExpandedStore = useExpandedStore({ key: 'GlobalNav' });
  return (
    <Flex
      direction="column"
      padding="0 0.75rem"
      paddingTop="0.75rem"
      alignItems="flex-end"
    >
      <IconButtonV2
        variant="v2-ghost"
        placement="right"
        icon={
          <Icon
            icon={layout.breakpoint === 'mobile' ? 'xmark-large' : 'sidebar'}
          />
        }
        aria-label={layout.globalNavOpen ? 'Collapse' : 'Expand'}
        onClick={() => {
          layout.setGlobalNavOpen(() => !layout.globalNavOpen);
          if (layout.breakpoint !== 'mobile') {
            navExpandedStore.set(
              !layout.globalNavOpen ? 'expanded' : 'collapsed',
            );
          }
        }}
      />
    </Flex>
  );
};

type GlobalNavProps = {
  children: React.ReactNode;
  isFixed?: boolean;
  offsetHeaderCount?: number;
  FlexProps?: React.ComponentProps<typeof Flex>;
};
export const GlobalNav = ({
  children,
  isFixed = false,
  offsetHeaderCount = 0,
  FlexProps,
}: GlobalNavProps) => {
  const layout = useLayoutContext();
  const borderRight = useColorModeValue(
    '1px solid gray.50',
    '1px solid gray.900',
  );

  // Collapse and expand nav bar as user window widens and shrinks
  useAutoCollapseOnBreakpoint();

  const content = (
    <>
      <CollapseButton />
      <Divider margin="0 0.75rem" width="auto" />
      <MenuTree
        FlexProps={{
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '0 0.75rem',
          paddingBottom: '1rem',
        }}
      >
        {children}
      </MenuTree>
      <Hide above="lg">
        <Divider margin="0 0.75rem" width="auto" />
        <LogoutButton />
      </Hide>
    </>
  );

  if (layout.breakpoint === 'mobile') {
    return (
      <Drawer
        isOpen={layout.globalNavOpen}
        size="full"
        isFullHeight
        placement="top"
        onClose={() => layout.setGlobalNavOpen((prev) => !prev)}
      >
        <DrawerOverlay>
          <DrawerContent>
            <DrawerBody padding="0.5rem">
              <VStack align="left" height="100%" width="100%">
                {content}
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    );
  }

  return (
    <>
      <Flex
        as="nav"
        direction="column"
        flexShrink={0}
        overflowY="auto"
        width={!layout.globalNavOpen ? '4rem' : '18.75rem'}
        borderRight={borderRight}
        boxShadow="0 0 4px hsl(0deg 0% 76% / 35%)"
        zIndex="docked"
        gap="0.75rem"
        height={
          isFixed
            ? `calc(100vh - ${HEADER_HEIGHT * offsetHeaderCount}px)`
            : undefined
        }
        position={isFixed ? 'fixed' : undefined}
        {...FlexProps}
      >
        {content}
      </Flex>
      {isFixed && (
        <Flex
          width={!layout.globalNavOpen ? '4rem' : '18.75rem'}
          height="100%"
          flexShrink={0}
        />
      )}
    </>
  );
};
